import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface QuestionTimer {
  remainingTime: number;
  completed: boolean;
}

interface SectionTimer {
  remainingTime: number;
  initialDuration: number;
}

interface TCSNQTTimerState {
  currentQuizId?: string;
  currentStudentId?: string;
  timers: {
    [sectionName: string]: {
      [questionId: string]: QuestionTimer;
    };
  };
  sectionTimers: {
    [sectionName: string]: SectionTimer;
  };
}

const initialState: TCSNQTTimerState = {
  currentQuizId: undefined,
  currentStudentId: undefined,
  timers: {},
  sectionTimers: {},
};

const tcsNqtTimerSlice = createSlice({
  name: "tcsNqtTimer",
  initialState,
  reducers: {
    initializeTimer: (
      state,
      action: PayloadAction<{
        quizId?: string;
        studentId?: string;
        sectionName: string;
        questionId: string;
        duration: number;
      }>,
    ) => {
      const { quizId, studentId, sectionName, questionId, duration } = action.payload;
      const quizChanged = Boolean(quizId && state.currentQuizId && state.currentQuizId !== quizId);
      const studentChanged = Boolean(studentId && state.currentStudentId && state.currentStudentId !== studentId);
      if (quizChanged || studentChanged) {
        state.timers = {};
        state.sectionTimers = {};
      }
      if (quizId) {
        state.currentQuizId = quizId;
      }
      if (studentId) {
        state.currentStudentId = studentId;
      }
      if (!state.timers[sectionName]) {
        state.timers[sectionName] = {};
      }
      // Only initialize if not already present — never reinitialize after refresh
      if (!state.timers[sectionName][questionId]) {
        state.timers[sectionName][questionId] = {
          remainingTime: duration,
          completed: false,
        };
      }
    },

    decrementTimer: (
      state,
      action: PayloadAction<{
        sectionName: string;
        questionId: string;
      }>,
    ) => {
      const { sectionName, questionId } = action.payload;
      const timer = state.timers[sectionName]?.[questionId];
      if (timer && timer.remainingTime > 0 && !timer.completed) {
        timer.remainingTime -= 1;
      }
    },

    markCompleted: (
      state,
      action: PayloadAction<{
        sectionName: string;
        questionId: string;
      }>,
    ) => {
      const { sectionName, questionId } = action.payload;
      const timer = state.timers[sectionName]?.[questionId];
      if (timer) {
        timer.remainingTime = 0;
        timer.completed = true;
      }
    },

    // Section timer actions
    initializeSectionTimer: (
      state,
      action: PayloadAction<{
        quizId?: string;
        studentId?: string;
        sectionName: string;
        durationInSeconds: number;
        initialRemainingTime?: number;
      }>,
    ) => {
      const { quizId, studentId, sectionName, durationInSeconds, initialRemainingTime } =
        action.payload;
      const quizChanged = Boolean(quizId && state.currentQuizId && state.currentQuizId !== quizId);
      const studentChanged = Boolean(studentId && state.currentStudentId && state.currentStudentId !== studentId);
      if (quizChanged || studentChanged) {
        state.timers = {};
        state.sectionTimers = {};
      }
      if (quizId) {
        state.currentQuizId = quizId;
      }
      if (studentId) {
        state.currentStudentId = studentId;
      }
      // Only initialize if not already present — preserve on refresh
      if (!state.sectionTimers[sectionName]) {
        state.sectionTimers[sectionName] = {
          remainingTime:
            initialRemainingTime !== undefined
              ? initialRemainingTime
              : durationInSeconds,
          initialDuration: durationInSeconds,
        };
      }
    },

    decrementSectionTimer: (
      state,
      action: PayloadAction<{ sectionName: string }>,
    ) => {
      const timer = state.sectionTimers[action.payload.sectionName];
      if (timer && timer.remainingTime > 0) {
        timer.remainingTime -= 1;
      }
    },

    deductFromSectionTimer: (
      state,
      action: PayloadAction<{ sectionName: string; amount: number }>,
    ) => {
      const timer = state.sectionTimers[action.payload.sectionName];
      if (timer) {
        timer.remainingTime = Math.max(
          0,
          timer.remainingTime - action.payload.amount,
        );
      }
    },

    setSectionRemainingTime: (
      state,
      action: PayloadAction<{ sectionName: string; remainingTime: number }>,
    ) => {
      const timer = state.sectionTimers[action.payload.sectionName];
      if (timer) {
        timer.remainingTime = action.payload.remainingTime;
      }
    },

    clearAllTimers: (state) => {
      state.timers = {};
      state.sectionTimers = {};
      state.currentQuizId = undefined;
      state.currentStudentId = undefined;
    },
  },
});

export const {
  initializeTimer,
  decrementTimer,
  markCompleted,
  initializeSectionTimer,
  decrementSectionTimer,
  deductFromSectionTimer,
  setSectionRemainingTime,
  clearAllTimers,
} = tcsNqtTimerSlice.actions;

export default tcsNqtTimerSlice.reducer;
